import { DEFAULT_ERROR_MESSAGE } from "@/types";
import api from "./api";

// Types for API requests and responses matching backend
export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
  site?: number; // 1 for Super Admin, 2 for User
}

export interface LoginResponse {
  accessToken: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  password: string;
  confirm_password: string;
  token: string;
}

// Backend ResponseData format
export interface ResponseData<T = any> {
  data: T;
  statusCode: number;
  message: string;
}

export interface ApiErrorResponse {
  status: number;
  message: string;
  data?: any;
}

let logoutCallback: null | (() => void) = null;

// Auth service class
export class AuthService {
  // Login API
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post("/auth/login", {
        email: credentials.email,
        password: credentials.password,
        remember: credentials.remember || false,
        site: credentials.site || 2,
      });

      // Backend returns accessToken, refreshToken được set vào httpOnly cookie
      return {
        accessToken: response.data.accessToken,
      };
    } catch (error: any) {
      // Backend throws HttpException with specific format
      if (error.response?.status === 401) {
        throw new Error("メールアドレスまたはパスワードが正しくありません。");
      }
      if (error.response?.status === 404) {
        throw new Error("ユーザーが見つかりません。");
      }
      if (error.response?.status === 403) {
        throw new Error("アクセスが拒否されました。");
      }
      if (error.response?.status === 400) {
        throw new Error(
          error?.response?.data?.message || DEFAULT_ERROR_MESSAGE
        );
      }
      throw new Error("ログインに失敗しました。もう一度お試しください。");
    }
  }

  // Refresh token API - gọi backend endpoint với cookie
  static async refreshToken(): Promise<LoginResponse> {
    try {
      // Backend sử dụng cookie 'refresh_token' được set từ httpOnly cookie
      const response = await api.post("/auth/refresh-token");

      // Backend returns direct tokens, not wrapped in ResponseData
      return {
        accessToken: response.data.accessToken,
      };
    } catch (error: any) {
      // Token hết hạn hoặc không hợp lệ
      if (error.response?.status === 401) {
        throw new Error("Refresh token expired or invalid");
      }
      throw new Error("Token refresh failed");
    }
  }

  // Forgot password API
  static async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await api.post("/auth/forgot-password", { email });

      // Backend returns ResponseData format for this endpoint
      const responseData: ResponseData = response.data;
      return {
        message:
          responseData.message ||
          "パスワードリセット用のリンクを送信しました。",
      };
    } catch (error: any) {
      // Handle backend error responses
      if (error.response?.status === 404) {
        throw new Error("このメールアドレスは登録されていません。");
      }
      if (error.response?.status === 400) {
        throw new Error("メールの送信に失敗しました。");
      }
      throw new Error("エラーが発生しました。もう一度お試しください。");
    }
  }

  // Verify reset token API
  static async verifyResetToken(
    token: string
  ): Promise<{ valid: boolean; message: string }> {
    try {
      const response = await api.get("/auth/verify-token", {
        params: { token },
      });

      // Backend returns ResponseData format
      const responseData: ResponseData = response.data;
      return {
        valid: true,
        message: responseData.message || "トークンが有効です。",
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        return {
          valid: false,
          message: "リセットトークンが無効または期限切れです。",
        };
      }
      return { valid: false, message: "トークンの検証に失敗しました。" };
    }
  }

  // Reset password API
  static async resetPassword(
    data: ResetPasswordRequest
  ): Promise<{ message: string }> {
    try {
      const response = await api.post("/auth/reset-password", data);

      // Backend returns ResponseData format
      const responseData: ResponseData = response.data;
      return {
        message:
          responseData.message || "パスワードが正常にリセットされました！",
      };
    } catch (error: any) {
      // Handle specific backend error responses
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.message?.includes("REENTERED_PASSWORD_INCORRECT")) {
          throw new Error("パスワードの確認が一致しません。");
        }
        throw new Error("入力されたデータが無効です。");
      }
      if (error.response?.status === 404) {
        throw new Error("リセットトークンが無効または期限切れです。");
      }
      throw new Error("エラーが発生しました。もう一度お試しください。");
    }
  }

  // logout
  static async logout() {
    await api.post("/auth/logout");
  }
}

export default AuthService;

export const setOnLogout = (callback: () => void) => {
  logoutCallback = callback;
};

export const triggerLogout = () => {
  if (logoutCallback) logoutCallback();
};
